import React, { useContext } from 'react';
import { compose } from 'recompose';
import { Grid, TextField, MenuItem, Checkbox, InputAdornment, IconButton, Popover, Button } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { css } from '@emotion/react';
import SearchIcon from '@material-ui/icons/Search';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { isValidDate } from '@/utilities/payPeriod';
import { usePayPeriodPicker, CUSTOM_RANGE_VALUE } from './hooks';
import Footer from './Footer';
import Table from './Table';
import { Context, ContextProvider } from './store';
import { H1, Body1 } from './blocks';
import { FILTER_DISPLAY_MAPPINGS, FILTERS } from './constants';
import { OnwardTab, OnwardTabContainer } from '@/components/Tabs';

// Pay-period dropdown with preset weeks (current + 10 prior, anchored to tenant cadence)
// and a "Custom Range" option that opens a popover with two date pickers. All state,
// validation, and handlers live in usePayPeriodPicker so this stays display-only.
const PayPeriodPicker = ({ state, callbacks }) => {
    const {
        anchorRef,
        popoverOpen,
        draftStart,
        draftEnd,
        setDraftStart,
        setDraftEnd,
        selected,
        selectedValue,
        canApply,
        endBeforeStart,
        handleChange,
        clearSelection,
        applyCustomRange,
        closePopover,
    } = usePayPeriodPicker({ state, callbacks });

    return (
        <>
            <TextField
                ref={anchorRef}
                variant="outlined"
                select
                fullWidth
                label="Pay Period"
                value={selectedValue}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                    endAdornment: selected ? (
                        <InputAdornment
                            position="end"
                            css={css`
                                margin-right: 20px;
                            `}
                        >
                            <IconButton
                                css={css`
                                    padding: 4px;
                                `}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    clearSelection();
                                }}
                            >
                                <CloseIcon
                                    css={css`
                                        font-size: 14px;
                                    `}
                                />
                            </IconButton>
                        </InputAdornment>
                    ) : null,
                }}
                onChange={handleChange}
                css={css`
                    background-color: white;
                    width: 100%;
                    padding-right: 2px;
                    margin-top: 0.2rem;
                `}
            >
                {state?.weeks.map(({ key, label }) => (
                    <MenuItem key={key} value={key}>
                        <Body1>{label}</Body1>
                    </MenuItem>
                ))}
                <MenuItem key={CUSTOM_RANGE_VALUE} value={CUSTOM_RANGE_VALUE}>
                    <Body1>{selected?.isCustom ? `Custom: ${selected.label}` : 'Custom Range...'}</Body1>
                </MenuItem>
            </TextField>
            <Popover
                open={popoverOpen}
                anchorEl={anchorRef.current}
                onClose={closePopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <Grid
                    container
                    direction="column"
                    css={css`
                        padding: 16px;
                        gap: 12px;
                        min-width: 280px;
                    `}
                >
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="Start"
                            format="MM/dd/yyyy"
                            value={draftStart}
                            onChange={(date) => setDraftStart(date)}
                            slotProps={{
                                textField: { fullWidth: true, InputLabelProps: { shrink: true } },
                            }}
                        />
                        <DatePicker
                            label="End"
                            format="MM/dd/yyyy"
                            value={draftEnd}
                            minDate={isValidDate(draftStart) ? draftStart : undefined}
                            onChange={(date) => setDraftEnd(date)}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    InputLabelProps: { shrink: true },
                                    error: endBeforeStart,
                                    helperText: endBeforeStart ? 'End date must be on or after the start date' : '',
                                },
                            }}
                        />
                    </LocalizationProvider>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-end"
                        css={css`
                            gap: 8px;
                        `}
                    >
                        <Button onClick={closePopover}>Cancel</Button>
                        <Button variant="contained" color="primary" disabled={!canApply} onClick={applyCustomRange}>
                            Apply
                        </Button>
                    </Grid>
                </Grid>
            </Popover>
        </>
    );
};

const StatusFilter = ({ state, callbacks }) => {
    return (
        <Grid
            direction="row"
            container
            css={css`
                display: flex;
                background-color: white;
            `}
        >
            <Grid
                direction="column"
                container
                css={css`
                    flex: 1;
                    flex-basis: 0;
                    align-content: center;
                `}
            >
                <OnwardTabContainer
                    value={state.filter.status}
                    onChange={(e, t) => {
                        callbacks.setFilter((prev) => {
                            return {
                                ...prev,
                                status: t,
                            };
                        });
                    }}
                    textColor="primary"
                    indicatorColor="primary"
                >
                    {Object.keys(FILTERS).map((key) => {
                        return <OnwardTab label={FILTER_DISPLAY_MAPPINGS[key]} value={key} />;
                    })}
                </OnwardTabContainer>
            </Grid>
        </Grid>
    );
};

export const Accounting = ({ context, title, TopBar, Footer }) => {
    const { state, loading, callbacks } = useContext(context);

    return (
        <>
            <Grid
                direction="column"
                container
                css={css`
                    width: 100%;
                    height: 100%;
                `}
            >
                <TopBar state={state} loading={loading} callbacks={callbacks} />

                <Grid
                    container
                    item
                    direction="row"
                    css={css`
                        padding: 40px;
                        padding-bottom: 0;
                        margin-bottom: 12px;
                    `}
                >
                    <Grid
                        container
                        direction="column"
                        css={css`
                            flex: 1;
                            flex-basis: 0;
                            justify-content: center;
                        `}
                    >
                        <H1>{title}</H1>
                    </Grid>

                    <Grid
                        container
                        direction="column"
                        css={css`
                            flex: 1;
                            flex-basis: 0;
                        `}
                    >
                        <Grid container direction="row">
                            <Grid
                                container
                                direction="column"
                                css={css`
                                    flex: 1;
                                    flex-basis: 0;
                                    overflow: hidden;
                                    margin-right: 16px;
                                `}
                            >
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    label="Search"
                                    placeholder="Search..."
                                    value={state?.filter?.searchTerm || ''}
                                    onChange={(e) => {
                                        callbacks.setFilter((prev) => {
                                            return {
                                                ...prev,
                                                searchTerm: e.target.value,
                                            };
                                        });
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    css={css`
                                        background-color: white;
                                        width: 100%;
                                        margin-top: 0.2rem;
                                    `}
                                />
                            </Grid>
                            {state.shippers ? (
                                <Grid
                                    container
                                    direction="column"
                                    css={css`
                                        flex: 1;
                                        flex-basis: 0;
                                        overflow: hidden;
                                        margin-right: 16px;
                                    `}
                                >
                                    <TextField
                                        variant="outlined"
                                        select
                                        fullWidth
                                        label="Shipper"
                                        value={state?.filter?.shippers || []}
                                        InputLabelProps={{ shrink: true }}
                                        onChange={(e) => {
                                            callbacks.setFilter((prev) => {
                                                return {
                                                    ...prev,
                                                    shippers: e.target.value,
                                                };
                                            });
                                        }}
                                        SelectProps={{
                                            multiple: true,
                                            renderValue: (selected) => {
                                                const map = Object.fromEntries(
                                                    state.shippers.map(({ label, value }) => {
                                                        return [value, label];
                                                    })
                                                );
                                                return selected.map((opt) => map[opt]).join(', ');
                                            },
                                        }}
                                        css={css`
                                            background-color: white;
                                            width: 100%;
                                            padding-right: 2px;
                                            margin-top: 0.2rem;
                                        `}
                                    >
                                        {state.shippers.map(({ label, value }) => (
                                            <MenuItem key={value} value={value}>
                                                <Checkbox
                                                    color="primary"
                                                    checked={(state?.filter?.shippers || []).indexOf(value) > -1}
                                                />
                                                <Body1>{label}</Body1>
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            ) : null}

                            {state.carriers ? (
                                <Grid
                                    container
                                    direction="column"
                                    css={css`
                                        flex: 1;
                                        flex-basis: 0;
                                        overflow: hidden;
                                        margin-right: 16px;
                                    `}
                                >
                                    <TextField
                                        variant="outlined"
                                        select
                                        fullWidth
                                        label="Carrier"
                                        value={state?.filter?.carriers || []}
                                        InputLabelProps={{ shrink: true }}
                                        onChange={(e) => {
                                            callbacks.setFilter((prev) => {
                                                return {
                                                    ...prev,
                                                    carriers: e.target.value,
                                                };
                                            });
                                        }}
                                        SelectProps={{
                                            multiple: true,
                                            renderValue: (selected) => {
                                                const map = Object.fromEntries(
                                                    state.carriers.map(({ label, value }) => {
                                                        return [value, label];
                                                    })
                                                );
                                                return selected.map((opt) => map[opt]).join(', ');
                                            },
                                        }}
                                        css={css`
                                            background-color: white;
                                            width: 100%;
                                            padding-right: 2px;
                                            margin-top: 0.2rem;
                                        `}
                                    >
                                        {state.carriers.map(({ label, value }) => (
                                            <MenuItem key={value} value={value}>
                                                <Checkbox
                                                    color="primary"
                                                    checked={(state?.filter?.carriers || []).indexOf(value) > -1}
                                                />
                                                <Body1>{label}</Body1>
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            ) : null}

                            {state.billTo ? (
                                <Grid
                                    container
                                    direction="column"
                                    css={css`
                                        flex: 1;
                                        flex-basis: 0;
                                        overflow: hidden;
                                        margin-right: 16px;
                                    `}
                                >
                                    <TextField
                                        variant="outlined"
                                        select
                                        fullWidth
                                        label="Bill To"
                                        value={state?.filter?.billTo || 'ALL'}
                                        InputLabelProps={{ shrink: true }}
                                        onChange={(e) => {
                                            const selected = e.target.value;
                                            callbacks.setFilter((prev) => {
                                                return {
                                                    ...prev,
                                                    billTo: selected === 'ALL' ? null : selected,
                                                };
                                            });
                                        }}
                                        css={css`
                                            background-color: white;
                                            width: 100%;
                                            padding-right: 2px;
                                            margin-top: 0.2rem;
                                        `}
                                    >
                                        <MenuItem value="ALL">
                                            <Body1>All</Body1>
                                        </MenuItem>
                                        {state.billTo.map(({ label, value }) => (
                                            <MenuItem key={value} value={value}>
                                                <Body1>{label}</Body1>
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            ) : null}

                            {state.weeks ? (
                                <Grid
                                    container
                                    direction="column"
                                    css={css`
                                        flex: 1;
                                        flex-basis: 0;
                                        overflow: hidden;
                                    `}
                                >
                                    <PayPeriodPicker state={state} callbacks={callbacks} />
                                </Grid>
                            ) : null}
                        </Grid>
                    </Grid>
                </Grid>

                <Grid
                    item
                    css={css`
                        overflow: hidden;
                        flex-grow: 1;
                        padding: 0 40px;
                    `}
                >
                    <Table state={state} loading={loading} callbacks={callbacks} />
                </Grid>

                <Footer state={state} loading={loading} callbacks={callbacks} />
            </Grid>
        </>
    );
};

const withContext = (Component) => (props) =>
    (
        <ContextProvider>
            <Component {...props} context={Context} title="Invoices" TopBar={StatusFilter} Footer={Footer} />
        </ContextProvider>
    );

export default compose(withContext)(Accounting);
